import { useCallback, useEffect, useMemo, useState } from "react";
import { tr } from "../i18n/tr";
import { CardSkeleton } from "../components/Skeletons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft, Loader2, BookOpen, FileText, ClipboardList, Download,
  Lock, CheckCircle2, Calendar, MessageCircle, PenLine, MapPin,
  Clock, Trophy, CircleDot, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import SessionChat from "../components/SessionChat";
import CourseModuleViewer from "../components/CourseModuleViewer";
import { useAppConfig, filterScormOnly } from "../hooks/useAppConfig";
import EvaluationForm from "../components/EvaluationForm";
import SignaturePad from "../components/SignaturePad";
import { useOfflineApi } from "../lib/offline/offlineApi";
import { OfflineBar } from "../components/offline/OfflineBar";
import { AttendanceKiosk } from "../components/offline/AttendanceKiosk";

const TABS = [
  { k: "cours", l: tr("lms.cours_33c1", "Cours"), icon: BookOpen },
  { k: "documents", l: tr("lms.documents_f281", "Documents"), icon: FileText, feature: "documents" },
  { k: "evaluations", l: tr("lms.evaluations_d1ec", "Evaluations"), icon: ClipboardList, feature: "evaluations" },
  { k: "chat", l: tr("lms.messagerie_d978", "Messagerie"), icon: MessageCircle, feature: "messagerie" },
  { k: "presence", l: tr("lms.ma_presence_3cb7", "Ma présence"), icon: PenLine, feature: "emargement" },
];

export default function MesFormationDetailPage() {
  const { session_id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Iter 321 — API hors ligne : lectures en cache, actions en file d'attente
  const api = useOfflineApi("student:" + session_id);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("cours");
  const [selectedSeqId, setSelectedSeqId] = useState(null);
  const [signingDoc, setSigningDoc] = useState(null);
  const [respondingEval, setRespondingEval] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/student/sessions/" + session_id);
      setData(r.data);
    } catch (e) { toast.error(e.offline ? "Formation non disponible hors ligne — préparez-la lorsque vous êtes connecté." : (e.response?.data?.detail || e.message)); }
    setLoading(false);
  // eslint-disable-next-line
  }, [session_id]);

  useEffect(() => { load(); }, [load]);

  // Auto-select first unlocked sequence
  useEffect(() => {
    if (!data || selectedSeqId) return;
    const seqs = data.session?.sequences || [];
    const firstUnlocked = seqs.find((s) => (data.unlocked_sequence_ids || []).includes(s.sequence_id));
    setSelectedSeqId(firstUnlocked?.sequence_id || seqs[0]?.sequence_id || null);
  }, [data, selectedSeqId]);

  const downloadDoc = async (doc_id, name) => {
    try {
      const r = await api.get("/lms/qualiopi/documents/" + doc_id + "/download", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = (name || doc_id) + ".pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const confirmSignature = async ({ signature_b64, signed_name, consent }) => {
    if (!signingDoc) return;
    try {
      await api.post(`/student/sessions/${session_id}/sign-document/${signingDoc.document_id}`, {
        signed_name, consent, signature_b64, role: "stagiaire",
      });
      toast.success(tr("lms.document_signe_avec_succes_29e6", "Document signe avec succes."));
      setSigningDoc(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const unlocked = useMemo(() => new Set(data?.unlocked_sequence_ids || []), [data]);
  const completedSet = useMemo(() => new Set(data?.enrollment?.completed_sequence_ids || []), [data]);
  const { features, isOn } = useAppConfig({ sessionId: session_id });
  const sequences = filterScormOnly(data?.session?.sequences || [], features);
  const selectedSeq = sequences.find((s) => s.sequence_id === selectedSeqId);

  if (loading || !data) {
    return <div className="py-6"><CardSkeleton /></div>;
  }

  const { session, enrollment, documents = [], evaluations = [] } = data;
  const pct = enrollment.progress || 0;

  return (
    <div className="min-h-screen" style={{ background: "radial-gradient(circle at 100% 0%, #1a1130 0%, #0b0811 60%)" }} data-testid="mes-formation-detail">
      {/* HERO */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 opacity-20" style={{
          background: session.cover_image
            ? `url(${session.cover_image}) center/cover`
            : "radial-gradient(circle at 80% 50%, var(--brand-primary, #f7c935) 0%, transparent 50%)"
        }} />
        <div className="relative max-w-7xl mx-auto px-6 py-8">
          <button onClick={() => navigate("/mes-formations")} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white mb-4" data-testid="back-to-list">
            <ArrowLeft className="w-3.5 h-3.5" /> {tr("lms.mes_formations_faaa", "Mes formations")}
          </button>
          <div className="grid lg:grid-cols-3 gap-6 items-end">
            <div className="lg:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: "var(--brand-primary, #f7c935)" }}>{session.program_title}</div>
              <h1 className="text-3xl lg:text-5xl font-black tracking-tight text-white leading-tight">{session.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-300">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {session.start_date} → {session.end_date}</span>
                {session.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {session.location}</span>}
                {session.modality && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {session.modality}</span>}
              </div>
            </div>
            <div className="bg-[#14101c]/70 backdrop-blur border border-white/10 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{tr("lms.ma_progression_4cd8", "Ma progression")}</span>
                <Trophy className="w-4 h-4" style={{ color: pct >= 100 ? "#10b981" : "var(--brand-primary, #f7c935)" }} />
              </div>
              <div className="text-4xl font-black text-white leading-none">{pct}%</div>
              <div className="bg-white/5 rounded-full h-1.5 overflow-hidden mt-3">
                <div className="h-full rounded-full transition-all" style={{
                  width: pct + "%",
                  background: pct >= 100 ? "#10b981" : "linear-gradient(90deg, var(--brand-primary, #f7c935), var(--brand-secondary, #c084fc))"
                }} />
              </div>
              <div className="mt-3 flex items-center justify-between text-[10px] text-slate-500">
                <span>{completedSet.size}/{sequences.length} modules</span>
                <span>{unlocked.size - completedSet.size} deblo.</span>
              </div>
            </div>
          </div>

          {/* TABS */}
          <div className="flex gap-1 border-b border-white/10 mt-6 -mb-px overflow-x-auto">
            {TABS.filter((t) => !t.feature || isOn(t.feature)).map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.k} onClick={() => setTab(t.k)} data-testid={"stud-tab-" + t.k}
                  className={"flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold border-b-2 whitespace-nowrap " + (tab === t.k ? "border-[var(--brand-primary,#f7c935)] text-[var(--brand-primary,#f7c935)]" : "border-transparent text-slate-400 hover:text-white")}>
                  <Icon className="w-3.5 h-3.5" />{t.l}
                  {t.k === "documents" && documents.length > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-white">{documents.length}</span>}
                  {t.k === "evaluations" && evaluations.length > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-white">{evaluations.length}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-5"><OfflineBar api={api} sessionId={session_id} role="student" enabled={isOn("hors_ligne")} /></div>
        {tab === "presence" && (
          <div className="max-w-3xl mx-auto" data-testid="stud-presence">
            <AttendanceKiosk api={api} sessionId={session_id} enrollments={[enrollment]} mode="self" ownEnrollmentId={enrollment.enrollment_id} />
          </div>
        )}
        {tab === "cours" && (
          <div className="grid lg:grid-cols-12 gap-6" data-testid="stud-cours">
            {/* Sidebar: module list */}
            <aside className="lg:col-span-4 xl:col-span-3">
              <div className="bg-[#14101c] border border-white/10 rounded-2xl p-3 sticky top-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2 px-2">{tr("lms.modules_de_formation_88e9", "Modules de formation")}</div>
                {sequences.length === 0 && <div className="text-center py-8 text-slate-500 text-xs">{tr("lms.aucun_module_publie_pour_0f6d", "Aucun module publie pour cette session.")}</div>}
                <div className="space-y-1">
                  {sequences.map((seq, i) => {
                    const isUnlocked = unlocked.has(seq.sequence_id);
                    const isDone = completedSet.has(seq.sequence_id);
                    const isActive = selectedSeqId === seq.sequence_id;
                    const newLesson = seq.lesson_id && (i === 0 || sequences[i - 1].lesson_id !== seq.lesson_id);
                    return (
                      <div key={seq.sequence_id || i}>
                      {newLesson && <div className="text-[10px] uppercase tracking-wider text-sky-400 font-bold px-2.5 pt-3 pb-1 truncate" data-testid={"stud-lesson-" + seq.lesson_id}>{seq.lesson_title}</div>}
                      <button
                        onClick={() => setSelectedSeqId(seq.sequence_id)}
                        className={"w-full text-left flex items-start gap-2 px-2.5 py-2 rounded-lg transition-colors " + (isActive ? "bg-[var(--brand-primary,#f7c935)]/10 border border-[var(--brand-primary,#f7c935)]/30" : "hover:bg-white/5 border border-transparent")}
                        data-testid={"stud-seq-" + seq.sequence_id}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {isDone ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : isUnlocked ? <CircleDot className="w-4 h-4" style={{ color: "var(--brand-primary, #f7c935)" }} /> : <Lock className="w-4 h-4 text-slate-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] text-slate-500 font-semibold">Séquence {i + 1}{seq.kind === "scorm" && <span className="ml-1.5 text-[8px] px-1 py-px rounded bg-sky-500/15 text-sky-300 uppercase font-bold" data-testid={"stud-seq-scorm-" + seq.sequence_id}>SCORM</span>}</div>
                          <div className={"text-xs font-semibold truncate " + (isActive ? "text-white" : "text-slate-300")}>{seq.title}</div>
                          {!isUnlocked && <div className="text-[9px] text-slate-600 mt-0.5">{tr("lms.en_attente_formateur_1a91", "En attente formateur")}</div>}
                        </div>
                        {isActive && <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0 mt-1" />}
                      </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </aside>

            {/* Main viewer */}
            <main className="lg:col-span-8 xl:col-span-9">
              <div className="bg-[#14101c] border border-white/10 rounded-2xl p-6 lg:p-8 min-h-[500px]">
                <CourseModuleViewer
                  api={api}
                  sessionId={session_id}
                  sequence={selectedSeq}
                  enrollmentId={data?.enrollment?.enrollment_id}
                  unlocked={selectedSeq ? unlocked.has(selectedSeq.sequence_id) : false}
                  done={selectedSeq ? completedSet.has(selectedSeq.sequence_id) : false}
                  onComplete={(info) => {
                    if (info?.queued && selectedSeq) {
                      const add = (ids) => Array.from(new Set([...(ids || []), selectedSeq.sequence_id]));
                      setData((d) => ({ ...d, enrollment: { ...d.enrollment, completed_sequence_ids: add(d.enrollment.completed_sequence_ids) } }));
                      api.patchCache("/student/sessions/" + session_id, (d) => ({ ...(d || {}), enrollment: { ...(d?.enrollment || {}), completed_sequence_ids: add(d?.enrollment?.completed_sequence_ids) } }));
                      return;
                    }
                    load();
                  }}
                />
              </div>
            </main>
          </div>
        )}

        {tab === "documents" && (
          <div className="max-w-3xl mx-auto space-y-3" data-testid="stud-documents">
            {documents.length === 0 && <Empty icon={FileText} label={tr("lms.aucun_document_pour_l_e110", "Aucun document pour l'instant.")} />}
            {documents.map((d) => {
              const signed = Object.keys(d.signatures || {}).some((k) => k.startsWith("stagiaire"));
              return (
                <div key={d.document_id} className="bg-[#14101c] border border-white/10 rounded-2xl p-5" data-testid={"stud-doc-" + d.document_id}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6" style={{ color: "var(--brand-primary, #f7c935)" }} />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-white text-sm">{d.doc_name || d.doc_type}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{d.doc_type} · {(d.created_at || "").slice(0, 10)}</div>
                      {signed && (
                        <span className="inline-flex items-center gap-1 mt-2 text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-bold uppercase">
                          <CheckCircle2 className="w-3 h-3" /> {tr("lms.signe_e02c", "Signe")}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button size="sm" variant="ghost" onClick={() => downloadDoc(d.document_id, d.doc_name)} className="border border-white/10 text-xs" data-testid={"stud-download-" + d.document_id}>
                        <Download className="w-3 h-3 mr-1" />PDF
                      </Button>
                      {!signed && ["convention", "contrat", "reglement_interieur", "livret_accueil", "emargement"].includes(d.doc_type) && (
                        <Button size="sm" onClick={() => setSigningDoc(d)} className="bg-[var(--brand-primary,#f7c935)] text-[#14101c] font-bold text-xs" data-testid={"stud-sign-" + d.document_id}>
                          <PenLine className="w-3 h-3 mr-1" /> {tr("lms.signer_d197", "Signer")}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "evaluations" && (
          <div className="max-w-3xl mx-auto space-y-3" data-testid="stud-evaluations">
            {evaluations.length === 0 && <Empty icon={ClipboardList} label={tr("lms.aucune_evaluation_pour_cette_019e", "Aucune evaluation pour cette session.")} />}
            {evaluations.map((e) => (
              <div key={e.evaluation_id} className="bg-[#14101c] border border-white/10 rounded-2xl p-5" data-testid={"stud-eval-" + e.evaluation_id}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-bold text-white text-sm">{e.title || "Evaluation"}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{e.kind || e.group_id || "evaluation"}</div>
                  </div>
                  {e.my_submission ? (
                    e.my_submission.grading?.graded_at ? (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 font-bold" title={e.my_submission.grading.comment || ""} data-testid={"stud-eval-grade-" + e.evaluation_id}>
                        Note : {e.my_submission.grading.score ?? "—"}/{e.my_submission.grading.max_score}{e.my_submission.grading.comment ? ` · ${e.my_submission.grading.comment}` : ""}
                      </span>
                    ) : (
                      <span className="text-[9px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 font-bold uppercase">{tr("lms.soumise_en_correction_a7e6", "Soumise · en correction")}</span>
                    )
                  ) : (
                    <Button size="sm" onClick={() => setRespondingEval(e)} className="bg-[var(--brand-primary,#f7c935)] text-[#14101c] font-bold text-xs" data-testid={"stud-respond-" + e.evaluation_id}>
                      {tr("lms.repondre_2468", "Repondre")}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "chat" && (
          <div className="max-w-4xl mx-auto bg-[#14101c] border border-white/10 rounded-2xl p-5" data-testid="stud-chat">
            <SessionChat sessionId={session.session_id} api={api} currentUser={user} />
          </div>
        )}
      </div>

      {/* SIGNING MODAL */}
      {signingDoc && (
        <Modal onClose={() => setSigningDoc(null)} title={`Signer: ${signingDoc.doc_name || signingDoc.doc_type}`}>
          <SignaturePad
            onConfirm={confirmSignature}
            onCancel={() => setSigningDoc(null)}
            hint={tr("lms.votre_signature_sera_horodatee_031d", "Votre signature sera horodatee (UTC), liee a votre IP et enregistree avec votre consentement explicite (eIDAS simple).")}
          />
        </Modal>
      )}

      {/* EVALUATION MODAL */}
      {respondingEval && (
        <Modal onClose={() => setRespondingEval(null)} title={respondingEval.title || "Evaluation"} wide>
          <EvaluationForm
            api={api}
            sessionId={session_id}
            evaluation={respondingEval}
            onDone={() => { setRespondingEval(null); load(); }}
          />
        </Modal>
      )}
    </div>
  );
}

function Empty({ icon: Icon, label }) {
  return (
    <div className="text-center py-12 border border-dashed border-white/10 rounded-2xl bg-[#14101c]/40">
      <Icon className="w-8 h-8 mx-auto mb-2 text-slate-600" />
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

function Modal({ onClose, title, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur p-4" onClick={onClose}>
      <div className={"bg-[#14101c] border border-white/10 rounded-2xl w-full " + (wide ? "max-w-2xl" : "max-w-lg") + " max-h-[92vh] overflow-y-auto"} onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#14101c] border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <h3 className="font-bold text-white text-sm">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white text-xl leading-none" data-testid="modal-close">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
